import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ApiClient from "../../utils/ApiClient";
import LineItemRow from "./LineItemRow";

export default function PODetailModal({ open, onClose, poNumber }) {
    const [poDetail, setPODetail] = useState(null);
    const [lineItems, setLineItems] = useState([]);
    const [shippedDiscrepancies, setShippedDiscrepancies] = useState([]);
    const [receivedDiscrepancies, setReceivedDiscrepancies] = useState([]);
    const [activityTimeline, setActivityTimeline] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState(null);
    const [bulkResolving, setBulkResolving] = useState(false);

    useEffect(() => {
        if (open && poNumber) {
            fetchData();
        }
    }, [open, poNumber, filter]);

    const fetchData = async () => {
        try {
            setLoading(true);
            // Extract base PO number for activity timeline (remove " RE" suffix if present)
            const basePO = poNumber.replace(/\s+[A-Z]{1,3}$/, '');
            const [detailResponse, itemsResponse, shippedResponse, receivedResponse, activityResponse] = await Promise.all([
                ApiClient.get(`/api/purchasing/pos/${poNumber}`),
                ApiClient.get(`/api/purchasing/pos/${poNumber}/items${filter && !filter.startsWith('shipped') && !filter.startsWith('received') && filter !== 'activity' ? `?filter=${filter}` : ''}`),
                ApiClient.get(`/api/purchasing/pos/${poNumber}/shipped-discrepancies`),
                ApiClient.get(`/api/purchasing/pos/${poNumber}/received-discrepancies`),
                ApiClient.get(`/api/purchasing/pos/${basePO}/activity`)
            ]);
            setPODetail(detailResponse.data);
            setLineItems(itemsResponse.data);
            setShippedDiscrepancies(shippedResponse.data);
            setReceivedDiscrepancies(receivedResponse.data);
            setActivityTimeline(activityResponse.data);
        } catch (error) {
            console.error('Error fetching PO detail:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleItemResolved = () => {
        fetchData();
    };

    const handleBulkResolve = async (resolutionType) => {
        if (!window.confirm(`Are you sure you want to mark all ${poDetail?.priceIssueCount || 0} price discrepancies as "${resolutionType === 'po_incorrect' ? 'PO Incorrect - Use Invoice' : 'Invoice Incorrect - Request Credit'}"?`)) {
            return;
        }

        try {
            setBulkResolving(true);
            const response = await ApiClient.post(`/api/purchasing/pos/${poNumber}/bulk-resolve-price`, {
                resolutionType: resolutionType,
                acceptedBy: 'System'
            });
            if (response.data.success) {
                alert(`Successfully resolved ${response.data.resolvedCount} items`);
                fetchData();
            }
        } catch (error) {
            console.error('Error bulk resolving:', error);
            alert('Error resolving discrepancies: ' + error.message);
        } finally {
            setBulkResolving(false);
        }
    };

    const formatCurrency = (value) => {
        if (value == null) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    const formatDate = (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString();
    };

    // Determine what data to show based on filter
    const isShowingShipped = filter === 'shipped_discrepancies';
    const isShowingReceived = filter === 'received_discrepancies';
    const isShowingActivity = filter === 'activity';
    const isShowingDiscrepancy = isShowingShipped || isShowingReceived;

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 w-full max-w-6xl">
                                {/* Header */}
                                <div className="bg-gray-50 px-4 py-4 sm:px-6 flex justify-between items-center border-b">
                                    <div>
                                        <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                                            PO #{poNumber}
                                        </Dialog.Title>
                                        {poDetail && (
                                            <p className="text-sm text-gray-500">
                                                Date: {formatDate(poDetail.poDate)} |
                                                Items: {poDetail.totalItems} |
                                                Resolved: {poDetail.resolvedCount || 0}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {/* Bulk Resolve Buttons - only show if there are price issues */}
                                        {poDetail && poDetail.priceIssueCount > 0 && (
                                            <>
                                                <button
                                                    type="button"
                                                    disabled={bulkResolving}
                                                    onClick={() => handleBulkResolve('po_incorrect')}
                                                    className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-green-500 disabled:opacity-50"
                                                >
                                                    {bulkResolving ? 'Processing...' : `Mark All (${poDetail.priceIssueCount}) PO Incorrect`}
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={bulkResolving}
                                                    onClick={() => handleBulkResolve('invoice_incorrect')}
                                                    className="rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-orange-500 disabled:opacity-50"
                                                >
                                                    {bulkResolving ? 'Processing...' : `Mark All (${poDetail.priceIssueCount}) Request Credit`}
                                                </button>
                                            </>
                                        )}
                                        <button
                                            type="button"
                                            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                                            onClick={onClose}
                                        >
                                            <XMarkIcon className="h-6 w-6" />
                                        </button>
                                    </div>
                                </div>

                                {/* Summary Stats */}
                                {poDetail && (
                                    <div className="px-4 py-4 sm:px-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 bg-gray-50 border-b">
                                        <div>
                                            <dt className="text-xs font-medium text-gray-500">PO Total</dt>
                                            <dd className="text-sm font-semibold text-gray-900">{formatCurrency(poDetail.poTotal)}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-xs font-medium text-gray-500">Invoice Total</dt>
                                            <dd className="text-sm font-semibold text-gray-900">{formatCurrency(poDetail.invoiceTotal)}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-xs font-medium text-gray-500">Variance</dt>
                                            <dd className={`text-sm font-semibold ${poDetail.variance > 0 ? 'text-red-600' : poDetail.variance < 0 ? 'text-green-600' : 'text-gray-900'}`}>
                                                {formatCurrency(poDetail.variance)}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-xs font-medium text-gray-500">Qty Ordered</dt>
                                            <dd className="text-sm font-semibold text-gray-900">{poDetail.qtyOrdered?.toLocaleString()}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-xs font-medium text-gray-500">Qty Shipped</dt>
                                            <dd className="text-sm font-semibold text-gray-900">{poDetail.qtyShipped?.toLocaleString()}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-xs font-medium text-gray-500">Price Issues</dt>
                                            <dd className="text-sm font-semibold text-red-600">{poDetail.priceIssueCount || 0}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-xs font-medium text-gray-500">SKU Issues</dt>
                                            <dd className="text-sm font-semibold text-yellow-600">{poDetail.skuIssueCount || 0}</dd>
                                        </div>
                                    </div>
                                )}

                                {/* Filter Tabs */}
                                <div className="px-4 py-3 sm:px-6 border-b bg-white">
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={() => setFilter(null)}
                                            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                                                filter === null
                                                    ? 'bg-indigo-100 text-indigo-700'
                                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                            }`}
                                        >
                                            All Items
                                        </button>
                                        <button
                                            onClick={() => setFilter('price_issues')}
                                            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                                                filter === 'price_issues'
                                                    ? 'bg-red-100 text-red-700'
                                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                            }`}
                                        >
                                            Price Issues
                                        </button>
                                        <button
                                            onClick={() => setFilter('sku_issues')}
                                            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                                                filter === 'sku_issues'
                                                    ? 'bg-yellow-100 text-yellow-700'
                                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                            }`}
                                        >
                                            SKU Issues
                                        </button>
                                        <button
                                            onClick={() => setFilter('resolved')}
                                            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                                                filter === 'resolved'
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                            }`}
                                        >
                                            Resolved
                                        </button>
                                        <button
                                            onClick={() => setFilter('shipped_discrepancies')}
                                            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                                                filter === 'shipped_discrepancies'
                                                    ? 'bg-orange-100 text-orange-700'
                                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                            }`}
                                        >
                                            Shipped Discrepancies ({shippedDiscrepancies.length})
                                        </button>
                                        <button
                                            onClick={() => setFilter('received_discrepancies')}
                                            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                                                filter === 'received_discrepancies'
                                                    ? 'bg-purple-100 text-purple-700'
                                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                            }`}
                                        >
                                            Received Discrepancies ({receivedDiscrepancies.length})
                                        </button>
                                        <button
                                            onClick={() => setFilter('activity')}
                                            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                                                filter === 'activity'
                                                    ? 'bg-blue-100 text-blue-700'
                                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                            }`}
                                        >
                                            Activity Timeline
                                        </button>
                                    </div>
                                </div>

                                {/* Table Content */}
                                <div className="max-h-[60vh] overflow-y-auto">
                                    {loading ? (
                                        <div className="flex items-center justify-center py-12">
                                            <div className="text-gray-500">Loading...</div>
                                        </div>
                                    ) : isShowingActivity ? (
                                        /* Activity Timeline */
                                        <div className="px-6 py-4">
                                            <div className="flow-root">
                                                <ul className="-mb-8">
                                                    {activityTimeline.map((event, idx) => (
                                                        <li key={idx}>
                                                            <div className="relative pb-8">
                                                                {idx !== activityTimeline.length - 1 && (
                                                                    <span className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                                                                )}
                                                                <div className="relative flex space-x-3">
                                                                    <div>
                                                                        <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                                                                            event.eventType === 'SHIPMENT' ? 'bg-blue-500' :
                                                                            event.eventType === 'REPLACEMENT' ? 'bg-green-500' :
                                                                            event.eventType === 'DAMAGE_REPORTED' ? 'bg-red-500' :
                                                                            event.eventType === 'RECEIVED' ? 'bg-purple-500' :
                                                                            'bg-gray-400'
                                                                        }`}>
                                                                            {event.eventType === 'SHIPMENT' && (
                                                                                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                                                                </svg>
                                                                            )}
                                                                            {event.eventType === 'REPLACEMENT' && (
                                                                                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                                                </svg>
                                                                            )}
                                                                            {event.eventType === 'DAMAGE_REPORTED' && (
                                                                                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                                </svg>
                                                                            )}
                                                                            {event.eventType === 'RECEIVED' && (
                                                                                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                                </svg>
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                                                                        <div>
                                                                            <p className="text-sm text-gray-900">
                                                                                {event.description}
                                                                                {event.invoiceNumber && (
                                                                                    <span className="ml-2 text-gray-500">
                                                                                        (Invoice #{event.invoiceNumber})
                                                                                    </span>
                                                                                )}
                                                                            </p>
                                                                            <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                                                                                {event.quantity != null && (
                                                                                    <span>Qty: {event.quantity}</span>
                                                                                )}
                                                                                {event.damaged != null && event.damaged > 0 && (
                                                                                    <span className="text-red-600">Damaged: {event.damaged}</span>
                                                                                )}
                                                                                {event.amount != null && (
                                                                                    <span>{formatCurrency(event.amount)}</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                                                            <time>{formatDate(event.eventDate)}</time>
                                                                            {event.status && (
                                                                                <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                                                                    event.status === 'COMPLETE' ? 'bg-green-100 text-green-800' :
                                                                                    event.status === 'REPORTED' ? 'bg-red-100 text-red-800' :
                                                                                    'bg-gray-100 text-gray-800'
                                                                                }`}>
                                                                                    {event.status}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                                {activityTimeline.length === 0 && (
                                                    <div className="text-center py-8 text-gray-500">
                                                        No activity found
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : isShowingDiscrepancy ? (
                                        /* Discrepancy Table */
                                        <table className="min-w-full divide-y divide-gray-300">
                                            <thead className="bg-gray-50 sticky top-0">
                                                <tr>
                                                    <th className="py-3 pl-4 pr-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                        {isShowingShipped ? 'Invoice' : 'Packing List'}
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                                        {isShowingShipped ? 'PO Qty' : 'Packing List Qty'}
                                                    </th>
                                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                                        {isShowingShipped ? 'Shipped Qty' : 'Received Qty'}
                                                    </th>
                                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Variance</th>
                                                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 bg-white">
                                                {(isShowingShipped ? shippedDiscrepancies : receivedDiscrepancies).map((item) => (
                                                    <tr key={item.itemId}>
                                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900">
                                                            {item.invoiceNumber}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                                                            {item.sku}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                                                            {item.expectedQty}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                                                            {item.actualQty}
                                                        </td>
                                                        <td className={`whitespace-nowrap px-3 py-4 text-sm text-right font-medium ${
                                                            item.variance > 0 ? 'text-green-600' : 'text-red-600'
                                                        }`}>
                                                            {item.variance > 0 ? '+' : ''}{item.variance}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                            {item.variance < 0 ? (
                                                                <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                                                                    Short {Math.abs(item.variance)}
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                                                                    Over {item.variance}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {(isShowingShipped ? shippedDiscrepancies : receivedDiscrepancies).length === 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="py-8 text-center text-gray-500">
                                                            No discrepancies found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    ) : (
                                        /* Line Items Table */
                                        <table className="min-w-full divide-y divide-gray-300">
                                            <thead className="bg-gray-50 sticky top-0">
                                                <tr>
                                                    <th className="py-3 pl-4 pr-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">PO Qty</th>
                                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">PO Price</th>
                                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Invoice Qty</th>
                                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Invoice Price</th>
                                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Variance</th>
                                                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Price Check</th>
                                                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">SKU Check</th>
                                                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Unit Check</th>
                                                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 bg-white">
                                                {lineItems.map((item) => (
                                                    <LineItemRow
                                                        key={item.itemId}
                                                        item={item}
                                                        onResolved={handleItemResolved}
                                                    />
                                                ))}
                                                {lineItems.length === 0 && (
                                                    <tr>
                                                        <td colSpan="11" className="py-8 text-center text-gray-500">
                                                            No items found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end border-t">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                        onClick={onClose}
                                    >
                                        Close
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
